/**
 * 文件路径: D:\XXY\MeiShan_Project\frontend\shared\api\auth.ts
 * 认证相关API服务
 */

import { httpClient, TokenManager } from './http'
import type { 
  LoginRequest, 
  LoginResponse, 
  RefreshTokenResponse, 
  User,
  ApiResponse 
} from '../types'

/**
 * 认证API服务类
 */
export class AuthService {
  /**
   * 用户登录
   * @param credentials - 登录凭据
   * @returns 登录响应信息
   */
  static async login(credentials: LoginRequest): Promise<LoginResponse> {
    // 登录接口的响应结构特殊，不包含在 'data' 字段中，因此直接使用axios实例
    const response = await httpClient.getAxiosInstance().post<LoginResponse>('/auth/login/', credentials);
    return response.data;
  }

  /**
   * 刷新访问令牌
   * @param refreshToken - 刷新令牌
   * @returns 新的访问令牌
   */
  static async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    // 令牌刷新接口的响应格式特殊，不包含在 'data' 字段中，因此直接使用axios实例
    const response = await httpClient.getAxiosInstance().post<RefreshTokenResponse>('/auth/token/refresh/', {
      refresh: refreshToken
    });
    return response.data;
  }

  /**
   * 获取当前用户信息
   * @returns 用户信息
   */
  static async getCurrentUser(): Promise<User> {
    // 使用 axios 实例直接请求，避免通用 httpClient 的 data 包装要求
    const axios = httpClient.getAxiosInstance()
    const token = TokenManager.getAccessToken?.() as string | null
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined
    const resp = await axios.get('/users/me/', { headers })
    
    // 🔍 调试日志：检查后端响应
    console.log('[AuthService] Backend response:', {
      fullResponse: resp.data,
      dataField: resp.data?.data,
      permissions: resp.data?.data?.permissions?.length,
      roles: resp.data?.data?.roles
    })
    
    // 后端统一响应格式：{ data: {...}, message: 'success', status: 'success' }
    // 这里兼容直接返回 data 或嵌套 data.data 的两种情况
    const data = (resp.data?.data ?? resp.data) as User
    
    // 🔍 调试日志：检查提取的数据
    console.log('[AuthService] Extracted user data:', {
      username: data.username,
      permissions: data.permissions?.length,
      roles: data.roles
    })
    
    return data
  }

  /**
   * 用户登出
   * 注意：JWT 登出在客户端处理（清除 token），后端无需 logout API
   * @param refreshToken - 刷新令牌（保留参数以保持接口兼容性）
   */
  static async logout(refreshToken?: string): Promise<void> {
    void refreshToken
    // JWT 登出在客户端处理，只需清除 localStorage 中的 token
    // 后端使用 JWT 黑名单机制（如需实现），可在此调用 API
    console.log('User logout: clearing tokens from client side')
  }

  /**
   * 修改密码
   * @param oldPassword - 旧密码
   * @param newPassword - 新密码
   */
  static async changePassword(oldPassword: string, newPassword: string): Promise<ApiResponse> {
    return await httpClient.post<ApiResponse>('users/change-password/', {
      old_password: oldPassword,
      new_password: newPassword
    })
  }

  /**
   * 重置密码（忘记密码）
   * @param email - 邮箱地址
   */
  static async resetPassword(email: string): Promise<ApiResponse> {
    return await httpClient.post<ApiResponse>('auth/reset-password/', {
      email
    })
  }

  /**
   * 确认重置密码
   * @param token - 重置令牌
   * @param newPassword - 新密码
   */
  static async confirmResetPassword(token: string, newPassword: string): Promise<ApiResponse> {
    return await httpClient.post<ApiResponse>('auth/reset-password/confirm/', {
      token,
      new_password: newPassword
    })
  }
}
